import {
    Form,
    DatePicker,
    Input,
    Button,
    Card,
    Typography,
    Breadcrumb,
    Modal,
    Table,
    Select,
    Tag,
    Grid,
} from "antd";
import { useState, useContext, useEffect, useCallback, useRef } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import { CloseOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Title } = Typography;
const { useBreakpoint } = Grid;

// ================= OffDayRecord Component =================
function OffDayRecord({
    backUrl,
    authData,
    isModalVisible,
    setIsModalVisible,
    leaveCancelReason,
    setLeaveCancelReason,
    selectedRecord,
    setSelectedRecord,
    targetId,
}) {
    const screens = useBreakpoint();
    const [filters, setFilters] = useState({
        fromDate: null,
        toDate: null,
        status: null,
    });
    const [offData, setOffData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);

    // Ref for scrolling to the highlighted row
    const highlightRowRef = useRef(null);

    const fetchOffData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${backUrl}/api/hr/compoffs/my-compoffs`, {
                headers: {
                    Authorization: `Bearer ${authData.access}`,
                    "Content-Type": "application/json",
                },
            });

            const mapped = (res.data.results || []).map((item, idx) => ({
                key: item.id ?? idx,
                id: item.id ?? idx,
                work_date: item.work_date ?? item.date ?? null,
                expiry_date: item.expiry_date ?? null,
                reason: item.reason ?? "",
                status:
                    typeof item.status === "string"
                        ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                        : item.status,
                approvals: item.approvals ?? [],
                cancellation_reason: item.cancellation_reason ?? "-",
            }));

            setOffData(mapped);
        } catch (err) {
            console.error("Error fetching off day data:", err);
            toast.error("Failed to load Off Day Working records");
        } finally {
            setLoading(false);
        }
    }, [authData.access, backUrl]);

    useEffect(() => {
        fetchOffData();
    }, [fetchOffData]);

    // ✅ Scroll to and highlight the target comp-off row after data loads
    useEffect(() => {
        if (targetId && offData.length > 0) {
            const targetIdNum = parseInt(targetId, 10);
            const found = offData.find((r) => r.id === targetIdNum);
            if (found) {
                console.log("🎯 OffDayRecord: Found target comp-off:", found);
                setTimeout(() => {
                    if (highlightRowRef.current) {
                        highlightRowRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                }, 300);
            }
        }
    }, [targetId, offData]);

    // Cancel handler
    const handleCancel = async () => {
        if (!selectedRecord) return;
        setCancelLoading(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/compoffs/${selectedRecord.id}/cancel/`,
                { cancellation_reason: leaveCancelReason },
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            toast.success(res.data.message || "Cancelled successfully!");
            setIsModalVisible(false);
            setLeaveCancelReason("");
            setSelectedRecord(null);
            fetchOffData(); // refresh dynamically
        } catch (error) {
            console.error("Error cancelling Off Day Working:", error);
            toast.error("Failed to cancel Off Day Working");
        } finally {
            setCancelLoading(false);
        }
    };

    const handleChange = (e) => setLeaveCancelReason(e.target.value);

    // Status color mapping (same as Permissions)
    const getStatusTag = (status) => {
        let color = "default";
        if (!status) return <Tag color="default">—</Tag>;

        switch (status.toLowerCase()) {
            case "pending approval":
                color = "blue";
                break;
            case "approved":
            case "available":
                color = "green";
                break;
            case "availed":
                color = "orange";
                break;
            case "rejected":
            case "expired":
                color = "red";
                break;
            case "cancelled":
                color = "gray";
                break;
            default:
                color = "default";
        }
        return <Tag color={color}>{status}</Tag>;
    };

    // Table columns
    const columns = [
        {
            title: "Worked Date",
            dataIndex: "work_date",
            key: "work_date",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "—"),
        },
        {
            title: "Expiring Date",
            dataIndex: "expiry_date",
            key: "expiry_date",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "—"),
        },
        {
            title: "Reason",
            key: "reason",
            width: 400,
            render: (record) => (
                <>
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                        {record.reason}
                    </div>

                    <br />

                    <br />
                    {record.cancellation_reason !== "-" && (
                        <div
                            style={{
                                marginTop: "4px",
                                fontWeight: 600,
                                color: "red",
                                backgroundColor: "#ffe6e6",
                                padding: "3px 6px",
                                borderRadius: "6px",
                                display: "inline-block",
                            }}
                        >
                            Cancellation Reason: {record.cancellation_reason}
                        </div>
                    )}
                </>

            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <span
                    style={{
                        fontWeight: 500,
                        color:
                            text === "Available"
                                ? "green"
                                : text === "Rejected"
                                    ? "red"
                                    : "#faad14", // Pending or others
                    }}
                >
                    {text}
                </span>
            ),
        },
        // {
        //     title: "Remarks",
        //     key: "remarks",
        //     width: 400,
        //     render: (_, record) => {
        //         const approvals = record.approvals || [];
        //         if (!approvals.length) return "-";

        //         return (
        //             <div
        //                 style={{
        //                     whiteSpace: "normal",
        //                     wordWrap: "break-word",
        //                     maxWidth: "400px",
        //                 }}
        //             >
        //                 {approvals.map((a, index) => (
        //                     <div
        //                         key={index}
        //                         style={{
        //                             borderBottom:
        //                                 index < approvals.length - 1
        //                                     ? "1px solid #ddd"
        //                                     : "none",
        //                             paddingBottom: "6px",
        //                             marginBottom: "6px",
        //                         }}
        //                     >
        //                         <strong>
        //                             {a.approver?.user?.first_name && a.approver?.user?.last_name
        //                                 ? `${a.approver.user.first_name} ${a.approver.user.last_name}`
        //                                 : a.approver?.user?.username}
        //                             :
        //                         </strong>{" "}
        //                         {a.rejection_reason !== "" ? (
        //                             <span style={{ color: "green" }}>{a.rejection_reason}</span>
        //                         ) : (
        //                             <span style={{ color: "green" }}>-</span>

        //                         )}
        //                         <br />

        //                         <br />
        //                         {a.cancellation_reason && (
        //                             <div
        //                                 style={{
        //                                     marginTop: "4px",
        //                                     fontWeight: 600,
        //                                     color: "red",
        //                                     backgroundColor: "#ffe6e6",
        //                                     padding: "3px 6px",
        //                                     borderRadius: "6px",
        //                                     display: "inline-block",
        //                                 }}
        //                             >
        //                                 Cancellation Remarks: {a.cancellation_reason}
        //                             </div>
        //                         )}
        //                     </div>
        //                 ))}
        //             </div>
        //         );
        //     },
        // },
        // {
        //     title: "Cancel",
        //     dataIndex: "cancel",
        //     render: (_, record) => (
        //         <Button
        //             type="text"
        //             danger
        //             icon={<CloseOutlined />}
        //             disabled={
        //                 !["Pending Approval", "Available"].includes(record.status)
        //             }
        //             onClick={() => {
        //                 setSelectedRecord(record);
        //                 setIsModalVisible(true);
        //                 setLeaveCancelReason("");
        //             }}
        //         />
        //     ),
        // },
    ];

    // Auto-updating filter logic
    const filteredData = offData.filter((item) => {
        const workDate = item.work_date ? dayjs(item.work_date) : null;
        if (!workDate?.isValid()) return false;

        const afterFrom =
            !filters.fromDate ||
            workDate.isSame(filters.fromDate, "day") ||
            workDate.isAfter(filters.fromDate);
        const beforeTo =
            !filters.toDate ||
            workDate.isSame(filters.toDate, "day") ||
            workDate.isBefore(filters.toDate);
        const matchStatus =
            !filters.status || item.status?.toLowerCase() === filters.status?.toLowerCase();

        return afterFrom && beforeTo && matchStatus;
    });

    return (
        <>
            {/* Cancel Modal */}
            <Modal
                title={`Cancel Off Day Working for ${selectedRecord
                    ? dayjs(selectedRecord.work_date).format("DD/MM/YYYY")
                    : "—"
                    }`}
                open={isModalVisible}
                footer={[
                    <Button
                        key="back"
                        onClick={() => {
                            setIsModalVisible(false);
                            setLeaveCancelReason("");
                            setSelectedRecord(null);
                        }}
                        disabled={cancelLoading}
                    >
                        Close
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        danger
                        onClick={handleCancel}
                        disabled={!leaveCancelReason.trim()}
                        loading={cancelLoading}
                    >
                        Submit
                    </Button>,
                ]}
                onCancel={() => {
                    setIsModalVisible(false);
                    setLeaveCancelReason("");
                    setSelectedRecord(null);
                }}
            >
                <TextArea
                    rows={4}
                    placeholder="Enter cancellation reason (required)"
                    value={leaveCancelReason}
                    onChange={handleChange}
                    onPressEnter={(e) => e.preventDefault()}
                />
            </Modal>

            {/* Filters */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: 16,
                }}
            >
                <DatePicker
                    format="DD/MM/YYYY"
                    placeholder="From Date"
                    style={{ width: screens.xs ? "100%" : "200px" }}
                    onChange={(date) =>
                        setFilters({
                            ...filters,
                            fromDate: date ? date.startOf("day") : null,
                        })
                    }
                    value={filters.fromDate}
                />

                <DatePicker
                    format="DD/MM/YYYY"
                    placeholder="To Date"
                    style={{ width: screens.xs ? "100%" : "200px" }}
                    onChange={(date) =>
                        setFilters({
                            ...filters,
                            toDate: date ? date.endOf("day") : null,
                        })
                    }
                    value={filters.toDate}
                />

                <Select
                    placeholder="Filter by Status"
                    style={{ width: screens.xs ? "100%" : "160px" }}
                    allowClear
                    onChange={(value) => setFilters({ ...filters, status: value })}
                    value={filters.status}
                >
                    <Select.Option value="Available">Available</Select.Option>
                    <Select.Option value="Availed">Availed</Select.Option>
                    <Select.Option value="Expired">Expired</Select.Option>
                </Select>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={filteredData}
                pagination={{ pageSize: 10 }}
                bordered
                loading={loading}
                scroll={{ x: "max-content" }}
                rowClassName={(record) => {
                    const targetIdNum = parseInt(targetId, 10);
                    return record.id === targetIdNum ? "highlighted-row" : "";
                }}
                onRow={(record) => {
                    const targetIdNum = parseInt(targetId, 10);
                    return {
                        ref: record.id === targetIdNum ? highlightRowRef : null,
                    };
                }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />
        </>
    );
}

// ================= Main OffDay Component =================
export default function OffDay() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [leaveCancelReason, setLeaveCancelReason] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [targetCompoffId, setTargetCompoffId] = useState(null);
    const screens = useBreakpoint();

    // ✅ Handle navigation from notifications - highlight specific comp-off row
    useEffect(() => {
        if (navigationParams?.compoffId && navigationParams?.openModal) {
            console.log("🎯 OffDay: Received navigation params:", navigationParams);
            setTargetCompoffId(navigationParams.compoffId);
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    const handleWorkingDateChange = (date) => {
        if (date) {
            const expiringDate = date.add(90, "day");
            form.setFieldsValue({ expiringDate });
        } else {
            form.setFieldsValue({ expiringDate: null });
        }
    };

    const onFinish = async (values) => {
        setSubmitLoading(true);
        try {
            const payload = {
                work_date: dayjs(values.workingDate).format("YYYY-MM-DD"),
                reason: values.reason,
                expiry_date: dayjs(values.expiringDate).format("YYYY-MM-DD"),
            };

            await axios.post(`${backUrl}/api/hr/compoffs/`, payload, {
                headers: {
                    Authorization: `Bearer ${authData.access}`,
                    "Content-Type": "application/json",
                },
            });

            toast.success("Off Day Working Request Submitted Successfully!");
            form.resetFields();
        } catch (error) {
            console.error("Error submitting Off Day Working:", error);
            const errorMsg =
                error.response?.data?.data ||
                error.response?.data?.detail ||
                error.response?.data?.message ||
                "Failed to submit Off Day Working request.";
            toast.error(errorMsg);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <>
            <OffDayRecord
                backUrl={backUrl}
                authData={authData}
                isModalVisible={isModalVisible}
                setIsModalVisible={setIsModalVisible}
                leaveCancelReason={leaveCancelReason}
                setLeaveCancelReason={setLeaveCancelReason}
                selectedRecord={selectedRecord}
                setSelectedRecord={setSelectedRecord}
                targetId={targetCompoffId}
            />
            {/* <ToastContainer position="top-right" autoClose={4000} />

            <Modal
                title="Off Day Working Records"
                open={isModalOpen}
                footer={null}
                onCancel={() => setIsModalOpen(false)}
                width={screens.lg ? 1200 : "95%"}
                style={{
                    top: screens.lg ? 50 : 10,
                    padding: "0 10px",
                }}
                bodyStyle={{
                    padding: screens.xs ? "10px" : "24px",
                }}
            >
                {isModalOpen && (
                    <OffDayRecord
                        backUrl={backUrl}
                        authData={authData}
                        isModalVisible={isModalVisible}
                        setIsModalVisible={setIsModalVisible}
                        leaveCancelReason={leaveCancelReason}
                        setLeaveCancelReason={setLeaveCancelReason}
                        selectedRecord={selectedRecord}
                        setSelectedRecord={setSelectedRecord}
                    />
                )}
            </Modal>

            <div
                className="flex flex-col items-center min-h-screen bg-gray-50 p-6"
                style={{ padding: screens.xs ? "10px" : "24px" }}
            >
                <div
                    className="w-full max-w-5xl mb-4"
                    style={{
                        width: screens.xs ? "100%" : "80%",
                        textAlign: screens.xs ? "center" : "left",
                    }}
                >
                    <Breadcrumb
                        items={[
                            {
                                title: (
                                    <span
                                        style={{
                                            cursor: "pointer",
                                            color: "#0e497b",
                                            fontSize: screens.xs ? "0.9rem" : "1rem",
                                        }}
                                        onClick={() => setIsModalOpen(true)}
                                    >
                                        Off Day Working Days
                                    </span>
                                ),
                            },
                        ]}
                    />
                </div>

                <center>
                    <Card
                        title={
                            <Title
                                level={4}
                                style={{
                                    margin: 0,
                                    textAlign: "center",
                                    color: "#0e497b",
                                    fontSize: screens.xs ? "1.2rem" : "1.5rem",
                                }}
                            >
                                Off Day Working Apply
                            </Title>
                        }
                        style={{
                            width: screens.xs ? "100%" : 600,
                            maxWidth: "100%",
                        }}
                        bordered
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            style={{ width: "100%" }}
                        >
                            <Form.Item
                                label="Working On the Date"
                                name="workingDate"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select the working date!",
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    onChange={handleWorkingDateChange}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Expiring Date"
                                name="expiringDate"
                                rules={[
                                    {
                                        required: true,
                                        message: "Expiring date will be auto-set!",
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{
                                        width: "100%",
                                        backgroundColor: "white",
                                    }}
                                    format="DD/MM/YYYY"
                                    disabled
                                />
                            </Form.Item>

                            <Form.Item
                                label="Reason"
                                name="reason"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter the reason!",
                                    },
                                ]}
                            >
                                <TextArea
                                    rows={4}
                                    placeholder="Enter your reason here..."
                                />
                            </Form.Item>

                            <Form.Item style={{ textAlign: "center" }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitLoading}
                                    style={{
                                        width: screens.xs ? "100%" : "400px",
                                        backgroundColor: "#0e497b",
                                    }}
                                >
                                    Submit Off Day Working Request
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </center>
            </div> */}
        </>
    );
}